/**
 * FinancialAccessGate — whole-page gate for money-only surfaces.
 *
 * Some pages ARE the money: Billing, the Starlink billing dashboard, project
 * financials. Masking every figure there leaves a shell of empty rows that's
 * more confusing than a straight "you don't have access" — so those pages are
 * gated outright, while pages where money is one field among many (deals,
 * jobs, customers) mask the figure via <Money> and stay usable.
 *
 * Mirrors DirectorAccessGate: spinner while the answer isn't authoritative,
 * denial only once it is, children only on grant (so the wrapped page's hooks
 * never mount in a denied state).
 */
import type { ReactNode } from 'react';
import { Loader2, EyeOff } from 'lucide-react';
import { useFinancialVisibility } from '@/context/FinancialVisibilityContext';

export function FinancialAccessGate({
  children,
  surface = 'This page',
}: {
  children: ReactNode;
  /** Named in the denial copy, e.g. "Billing". */
  surface?: string;
}) {
  const { canViewFinancials, checking } = useFinancialVisibility();

  if (checking) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-secondary animate-spin" />
      </div>
    );
  }

  if (!canViewFinancials) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <EyeOff className="w-12 h-12 text-theme-muted mx-auto mb-3" />
          <h2 className="text-lg font-bold text-theme-primary">
            Financial figures hidden
          </h2>
          <p className="text-sm text-theme-muted mt-1">
            {surface} shows money, and your account isn&apos;t set up to see
            financial figures. A director or admin can turn this on in
            Settings → Financials.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
